import { motion } from "framer-motion";
import { Cookie, Settings, Shield, Clock, Globe, ToggleLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const cookieTypes = [
  {
    icon: <Settings />,
    title: "Essential Cookies",
    description: "These cookies are necessary for the website to function and cannot be switched off in our systems.",
    required: true,
    examples: [
      "Session management",
      "Load balancing",
      "Security tokens",
      "User preferences"
    ]
  },
  {
    icon: <Globe />,
    title: "Performance Cookies",
    description: "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.",
    required: false,
    examples: [
      "Page load times",
      "Site traffic analytics",
      "Error reporting",
      "User behavior patterns"
    ]
  },
  {
    icon: <ToggleLeft />,
    title: "Functional Cookies",
    description: "These cookies enable the website to provide enhanced functionality and personalization.",
    required: false,
    examples: [
      "Language preferences",
      "Theme settings",
      "Form auto-fill data",
      "Live chat services"
    ]
  },
  {
    icon: <Shield />,
    title: "Targeting Cookies",
    description: "These cookies may be set through our site by our advertising partners to build a profile of your interests.",
    required: false,
    examples: [
      "Advertisement tracking",
      "Social media sharing",
      "Marketing campaigns",
      "User demographics"
    ]
  }
];

const Cookies = () => {
  const lastUpdated = "March 15, 2024";

  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Cookie Policy"
        description="Learn about how and why we use cookies to improve your browsing experience."
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-16">
          <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mb-6">
            This Cookie Policy explains how PromptPerfect ("we", "us", and "our") uses cookies and similar technologies to recognize you when you visit our website. It explains what these technologies are and why we use them, as well as your rights to control our use of them.
          </p>
          <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
            Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.
          </p>
        </div>

        {/* Cookie Types */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {cookieTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card p-6 sm:p-8 rounded-xl shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  {type.icon}
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-foreground">{type.title}</h2>
                  {type.required && (
                    <span className="text-sm text-primary">Required</span>
                  )}
                </div>
              </div>
              <p className="text-muted-foreground text-sm sm:text-base mb-4">{type.description}</p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <h3 className="font-medium text-foreground mb-2">Examples:</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 text-sm sm:text-base">
                  {type.examples.map((example, i) => (
                    <li key={i}>{example}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Cookie Management */}
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Managing Your Cookie Preferences</h2>
          <p className="text-muted-foreground text-base sm:text-lg mb-6">
            You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.
          </p>
          <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
            Update Cookie Preferences
          </Button>
        </div>

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Changes to This Cookie Policy</h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page and updating the "last updated" date at the top of this Cookie Policy.
            </p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Contact Us</h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              If you have any questions about our Cookie Policy, please contact us at:
              <br className="hidden sm:block" />
              Email: privacy@promptperfect.ai
              <br className="hidden sm:block" />
              Address: 123 AI Street, Tech City, TC 12345
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Cookies; 